import React, { useEffect, useState } from 'react';
import Icon from 'components/AppIcon';
import OpportunitiesFormModal from './OpportunitiesFormModal';
import DeleteConfirmModal from '../../../components/ui/DeleteConfirmModal';

const STAGE_COLORS = [
  { bg: '#DBEAFE', color: '#1D4ED8' },
  { bg: '#FEF3C7', color: '#B45309' },
  { bg: '#EDE9FE', color: '#6D28D9' },
  { bg: '#CFFAFE', color: '#0E7490' },
  { bg: '#DCFCE7', color: '#166534' },
  { bg: '#FEE2E2', color: '#991B1B' },
];

const DEFAULT_STAGE_NAMES = {
  1: 'Prospecting',
  2: 'Qualification',
  3: 'Proposal',
  4: 'Negotiation',
  5: 'Closed Won',
  6: 'Closed Lost',
};

function formatRupees(value) {
  if (value >= 10000000) return `₹${(value / 10000000)?.toFixed(1)}Cr`;
  if (value >= 100000) return `₹${(value / 100000)?.toFixed(1)}L`;
  return `₹${value?.toLocaleString('en-IN')}`;
}

function mapOpportunityFromApi(item) {
  return {
    id: item?.id,
    name: item?.name || '',
    company: item?.accountName || '',
    value: Number(item?.amount || 0),
    probability: Number(item?.probabilityPercent || 0),
    stage: Number(item?.stage ?? 1),
    rep: item?.ownerName || 'Unassigned',
    closeDate: item?.expectedCloseDateUtc ? new Date(item.expectedCloseDateUtc)?.toISOString()?.slice(0, 10) : '',
    source: item?.description || '—',
  };
}

export default function OpportunitiesTable({ searchQuery, stageFilter, stages, isFormOpen, setIsFormOpen, selectedOpportunity, setSelectedOpportunity, initialOpportunities = [], onSubmitOpportunity, onDeleteOpportunity, canDelete = true, onAddNew }) {
  const [sortField, setSortField] = useState('value');
  const [sortDir, setSortDir] = useState('desc');
  const [opportunities, setOpportunities] = useState([]);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  useEffect(() => {
    setOpportunities((initialOpportunities || []).map(mapOpportunityFromApi));
  }, [initialOpportunities]);

  const filtered = opportunities?.filter((opp) => {
    const q = searchQuery?.toLowerCase() || '';
    const matchesSearch = !q || [opp?.name, opp?.company, opp?.rep, opp?.source]?.some((f) => f?.toLowerCase()?.includes(q?.toLowerCase()));
    const matchesStage = !stageFilter || stageFilter === 'all' || opp?.stage === stageFilter;
    return matchesSearch && matchesStage;
  });

  const sorted = [...(filtered || [])]?.sort((a, b) => {
    let av = a?.[sortField], bv = b?.[sortField];
    if (typeof av === 'string') { av = av?.toLowerCase(); bv = bv?.toLowerCase(); }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  const handleEditOpportunity = (opp) => {
    setSelectedOpportunity(opp);
    setIsFormOpen(true);
  };

  const handleDeleteOpportunity = (id) => {
    setDeleteTargetId(id);
    setIsDeleteOpen(true);
  };

  const handleFormSubmit = async (formData) => {
    if (onSubmitOpportunity) {
      await onSubmitOpportunity(formData);
    } else if (selectedOpportunity) {
      setOpportunities((prev) => prev?.map((o) => o?.id === selectedOpportunity?.id ? { ...o, ...formData } : o));
      setIsFormOpen(false);
      setSelectedOpportunity(null);
      return;
    } else {
      const newId = Math.max(...opportunities?.map((o) => o?.id), 0) + 1;
      setOpportunities((prev) => [...prev, { id: newId, ...formData }]);
      setIsFormOpen(false);
      setSelectedOpportunity(null);
      return;
    }
  };

  const handleConfirmDelete = async () => {
    if (deleteTargetId && onDeleteOpportunity) {
      await onDeleteOpportunity(deleteTargetId);
    }

    setOpportunities((prev) => prev?.filter((o) => o?.id !== deleteTargetId));
    setIsDeleteOpen(false);
    setDeleteTargetId(null);
  };

  const SortIcon = ({ field }) => (
    <Icon
      name={sortField === field ? (sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'}
      size={12}
      color={sortField === field ? 'var(--color-primary)' : 'var(--color-text-secondary)'}
    />
  );

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--color-border)', background: 'var(--color-card)' }}>
      <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        <table className="w-full" style={{ minWidth: '750px' }}>
          <thead>
            <tr style={{ background: 'var(--color-muted)', borderBottom: '1px solid var(--color-border)' }}>
              {[
                { label: 'Opportunity', field: 'name' },
                { label: 'Company', field: 'company' },
                { label: 'Deal Value', field: 'value' },
                { label: 'Probability', field: 'probability' },
                { label: 'Stage', field: 'stage' },
                { label: 'Assigned Rep', field: 'rep' },
                { label: 'Close Date', field: 'closeDate' },
              ]?.map(({ label, field }) => (
                <th
                  key={field}
                  className="px-4 py-3 text-left cursor-pointer select-none"
                  onClick={() => toggleSort(field)}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="font-caption text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-secondary)' }}>
                      {label}
                    </span>
                    <SortIcon field={field} />
                  </div>
                </th>
              ))}
              <th className="px-4 py-3 text-left">
                <span className="font-caption text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-secondary)' }}>
                  Actions
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted?.map((opp, idx) => {
              const stageName = stages?.find((s) => s.id === opp?.stage)?.name || DEFAULT_STAGE_NAMES[opp?.stage] || `Stage ${opp?.stage}`;
              const stageCfg = STAGE_COLORS[(opp?.stage - 1) % STAGE_COLORS.length] || STAGE_COLORS[0];
              return (
                <tr
                  key={opp?.id}
                  style={{
                    background: idx % 2 === 0 ? 'transparent' : 'var(--color-surface-2)',
                    borderBottom: '1px solid var(--color-border)',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-muted)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = idx % 2 === 0 ? 'transparent' : 'var(--color-surface-2)'; }}
                >
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="font-caption font-semibold text-sm" style={{ color: 'var(--color-foreground)' }}>{opp?.name}</span>
                      <span className="font-caption text-xs" style={{ color: 'var(--color-text-secondary)' }}>{opp?.source}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-caption text-sm" style={{ color: 'var(--color-foreground)' }}>{opp?.company}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-caption font-bold text-sm" style={{ color: 'var(--color-primary)' }}>
                      {formatRupees(opp?.value)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center justify-between">
                        <span className="font-caption text-xs font-semibold" style={{ color: 'var(--color-foreground)' }}>
                          {opp?.probability}%
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--color-border)', width: 80 }}>
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${opp?.probability}%`,
                            background: opp?.probability >= 70 ? 'var(--color-success)' : opp?.probability >= 40 ? 'var(--color-warning)' : 'var(--color-error)',
                          }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className="px-2.5 py-1 rounded-full font-caption text-xs font-semibold"
                      style={{ background: stageCfg?.bg, color: stageCfg?.color }}
                    >
                      {stageName}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-caption text-sm" style={{ color: 'var(--color-foreground)' }}>{opp?.rep}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Icon name="Calendar" size={12} color="var(--color-text-secondary)" />
                      <span className="font-caption text-sm" style={{ color: 'var(--color-foreground)' }}>{opp?.closeDate}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <button
                        className="flex items-center justify-center rounded-md transition-colors hover:bg-muted"
                        style={{ width: 28, height: 28, color: 'var(--color-primary)' }}
                        title="Edit"
                        onClick={() => handleEditOpportunity(opp)}
                      >
                        <Icon name="Edit2" size={13} color="currentColor" />
                      </button>
                      {canDelete && (
                        <button
                          className="flex items-center justify-center rounded-md transition-colors hover:bg-red-50"
                          style={{ width: 28, height: 28, color: '#DC2626' }}
                          title="Delete"
                          onClick={() => handleDeleteOpportunity(opp?.id)}
                        >
                          <Icon name="Trash2" size={13} color="currentColor" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {sorted?.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <Icon name="SearchX" size={32} color="var(--color-text-secondary)" />
                    <span className="font-caption text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      {opportunities?.length === 0 ? 'No opportunities yet' : 'No opportunities match your filters'}
                    </span>
                    {opportunities?.length === 0 && onAddNew && (
                      <button
                        onClick={onAddNew}
                        className="mt-2 px-4 py-2 rounded-lg font-caption text-sm font-medium transition-colors"
                        style={{ background: 'var(--color-primary)', color: '#fff' }}
                      >
                        Add your first opportunity
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      <OpportunitiesFormModal
        isOpen={isFormOpen}
        opportunity={selectedOpportunity}
        stages={stages}
        onClose={() => {
          setIsFormOpen(false);
          setSelectedOpportunity(null);
        }}
        onSubmit={handleFormSubmit}
      />
      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        itemName="Opportunity"
        onClose={() => {
          setIsDeleteOpen(false);
          setDeleteTargetId(null);
        }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
